import { Button, Grid, Select, Text, Textarea } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { useForm } from "@mantine/form"
import { CalendarBlankIcon } from "@phosphor-icons/react"
import { yupResolver } from "mantine-form-yup-resolver"
import dayjs from "dayjs"
import React from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import useSWR from "swr"
import * as yup from "yup"
import { i18n } from "@dehub/theme"
import { BackOrderApi, DeliveryApi } from "../../apis"
import { IAuth } from "../../store/Auth"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { OrderActionCard } from "../order/order-action-card"
import { useSalesActions } from "../order/order-actions"
import { ReasonTooltip } from "../ui"
import { StackLines } from "./line"

const schema = yup.object({
  warehouseId: yup.string().required(() => i18n.t("order:stackForm.warehouseRequired")).nullable(),
  deliveryDate: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
  senderAdditionalNote: yup.string().nullable(),
  lines: yup.array().of(
    yup.object({
      quantity: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
      buyerBusinessId: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
      supplierBusinessId: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
      receiverBranchId: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
      receiverStaffId: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
    }),
  ),
})

type IFormData = {
  warehouseId: string
  deliveryDate: string
  senderAdditionalNote: string
  lines: []
}

/**
 * Нөхөн захиалга үүсгэх (Ord_SalesModals 2.4.5). The backend (order/backorder/create.ts) needs, per line,
 * the source warehouse and the buyer's receiver BRANCH id; the warehouse is chosen once here, the branch per line.
 */
export function StackForm({ action, onCancel, reload }: { action: any[]; onCancel: () => void; reload: any }) {
  const { t } = useTranslation(["order", "common"])
  const [loading, setLoading] = React.useState<boolean>(false)
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const { permissionState } = useSalesActions()
  const perm = permissionState("backorder")

  const orderIds = ((action && action[1]) || []).map((c: { id: string }) => c?.id)

  const { data: LinesData } = useSWR(`/order/backorder/lines/${orderIds.join(",")}`, async () => {
    try {
      return await BackOrderApi.get(orderIds)
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    }
  })

  const { data: warehouses } = useSWR(user?.currentBusinessId ? ["backorder.warehouse-select", user.currentBusinessId] : null, async () => {
    try {
      return (await DeliveryApi.warehouseSelect({ businessId: user!.currentBusinessId!, query: null, limit: 50 })) as { id: string; name: string }[]
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    }
  })

  const form = useForm<IFormData>({
    mode: "controlled",
    initialValues: { warehouseId: "", deliveryDate: "", senderAdditionalNote: "", lines: [] },
    validate: yupResolver(schema),
  })

  const onSubmit = async (value: IFormData) => {
    if (!value?.lines || value.lines.length === 0) {
      message.error(t("order:stackForm.noItems"))
      return
    }
    setLoading(true)
    try {
      await BackOrderApi.create({
        orderIds,
        deliveryDate: new Date(value.deliveryDate).toISOString(),
        senderAdditionalNote: value.senderAdditionalNote || null,
        lines: (value.lines as any[]).map((c) => ({
          variantId: c.variantId,
          warehouseId: value.warehouseId,
          buyerBusinessId: c?.buyerBusinessId,
          supplierBusinessId: c?.supplierBusinessId,
          receiverBranchId: c?.receiverBranchId,
          receiverStaffId: c?.receiverStaffId,
          quantity: c?.quantity,
        })),
      })
      message.success(t("order:stackForm.success"))
      onCancel()
      reload()
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    } finally {
      setLoading(false)
    }
  }

  return (
    <OrderActionCard
      title={t("order:stackForm.title")}
      onClose={onCancel}
      loading={loading}
      footer={
        <>
          <Button variant="default" onClick={onCancel}>{t("common:actions.dismiss")}</Button>
          <ReasonTooltip reason={perm.reason}>
            <Button
              onClick={() => {
                const { hasErrors } = form.validate()
                if (!hasErrors) onSubmit(form.values)
              }}
            >
              {t("order:stackForm.createButton")}
            </Button>
          </ReasonTooltip>
        </>
      }
    >
      <Grid>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Select
            {...form.getInputProps("warehouseId")}
            label={t("order:stackForm.warehouse")}
            placeholder={t("order:stackForm.warehousePlaceholder")}
            data={(warehouses || []).map((w) => ({ value: w.id, label: w.name }))}
            searchable
            required
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <DatePickerInput
            {...form.getInputProps("deliveryDate")}
            value={form.values.deliveryDate ? new Date(form.values.deliveryDate) : null}
            onChange={(v) => form.setFieldValue("deliveryDate", v ? dayjs(v).format() : "")}
            label={t("order:stackForm.receiveDate")}
            placeholder={t("order:stackForm.receiveDatePlaceholder")}
            required
            rightSection={<CalendarBlankIcon />}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Textarea
            {...form.getInputProps("senderAdditionalNote")}
            label={t("order:stackForm.additionalNote")}
            placeholder={t("order:stackForm.additionalNotePlaceholder")}
            maxLength={255}
            autosize
            minRows={1}
            maxRows={4}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <Text fw={650} fz={14}>{t("order:stackForm.batchInfo")}</Text>
        </Grid.Col>
        <Grid.Col span={12}>
          <StackLines list={{ LinesData }} values={form.values} setFieldValue={form.setFieldValue} />
        </Grid.Col>
      </Grid>
    </OrderActionCard>
  )
}
